import * as dayjs from 'dayjs';
import { TimeZone } from '../../time/timezone.js';
import { MapUtils } from '../../utility/map-utils.js';
import {
  DataModel, DataModelBuilder, isSameData, isSameList, SerializationContext, SerializedData,
} from '../dto/dto.js';
import { SyncEvent, SyncEventSource } from '../sync-event.js';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { OpenAccessPass } from './open-access-pass/open-access-pass.js';
import { ElectronicDeliveryMethod } from './electronic-delivery-method.js';

export class OpenAccessPassBatchCreatedEvent extends SyncEvent {
  static readonly TYPE_ID: string = '59aee5a2bb1949b08d240fba6836453e';

  private constructor(
    public readonly uniqueId: string,
    public readonly creatorId: string | null,
    public readonly deviceId: string | null,
    public readonly creationDate: dayjs.Dayjs,
    public readonly source: SyncEventSource,
    public readonly passes: OpenAccessPass[],
    public readonly deliveryMethod: ElectronicDeliveryMethod,
  ) {
    super();
  }

  static create(args: {
    uniqueId: string,
    creatorId: string | null,
    deviceId: string | null,
    creationDate: dayjs.Dayjs,
    source: SyncEventSource,
    passes: OpenAccessPass[],
    deliveryMethod?: ElectronicDeliveryMethod,
  }): OpenAccessPassBatchCreatedEvent {
    return new OpenAccessPassBatchCreatedEvent(
        args.uniqueId,
        args.creatorId,
        args.deviceId,
        args.creationDate,
        args.source,
        args.passes,
        args.deliveryMethod ?? ElectronicDeliveryMethod.Printer,
    );
  }

  get $typeId(): string {
    return OpenAccessPassBatchCreatedEvent.TYPE_ID;
  }

  static fromMap(context: SerializationContext, map: SerializedData): OpenAccessPassBatchCreatedEvent {
    return new OpenAccessPassBatchCreatedEvent(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.getOrNull<string>(map, 'creatorId'),
        MapUtils.getOrNull<string>(map, 'deviceId'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        toEnumFromString(MapUtils.get<string>(map, 'source'), SyncEventSource),
        MapUtils.getList<OpenAccessPass>(map, 'passes', (value) => {
          return context.fromData<OpenAccessPass>(value as SerializedData);
        }),
        toEnumFromString(
            MapUtils.getStringOrNull(map, 'deliveryMethod') ?? fromEnumAsString(ElectronicDeliveryMethod.Printer),
            ElectronicDeliveryMethod,
        ),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'creatorId': this.creatorId,
      'deviceId': this.deviceId,
      'creationDate': context.encodeDate(this.creationDate),
      'source': fromEnumAsString(this.source),
      'passes': this.passes.map((e) => context.toData(e)),
      'deliveryMethod': fromEnumAsString(this.deliveryMethod),
    };
  }

  clone(): this {
    return new OpenAccessPassBatchCreatedEvent(
        this.uniqueId,
        this.creatorId,
        this.deviceId,
        TimeZone.cloneDate(this.creationDate),
        this.source,
        this.passes.map((e) => e.clone()),
        this.deliveryMethod,
    ) as this;
  }

  isSame(other: OpenAccessPassBatchCreatedEvent): boolean {
    if(this === other) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.creatorId === this.creatorId &&
        other.deviceId === this.deviceId &&
        other.creationDate === this.creationDate &&
        other.source === this.source &&
        other.deliveryMethod === this.deliveryMethod &&
        isSameList(other.passes, this.passes, isSameData);
  }

  static verifyType(dm: DataModel): boolean {
    return dm instanceof OpenAccessPassBatchCreatedEvent;
  }

  static assertType(dm: DataModel): dm is OpenAccessPassBatchCreatedEvent {
    return dm instanceof OpenAccessPassBatchCreatedEvent;
  }
}

export class OpenAccessPassBatchCreatedEventBuilder extends DataModelBuilder<OpenAccessPassBatchCreatedEvent> {
  build(context: SerializationContext, map: SerializedData): OpenAccessPassBatchCreatedEvent {
    return OpenAccessPassBatchCreatedEvent.fromMap(context, map);
  }
}
